import React, { useState, useEffect, useRef } from 'react';

// ============================================================================
// AI WELCOME - MULTILINGUAL ASYLUM RECEPTION ASSISTANT
// With Real-time Translation, Pre-set Questions & Pre/Post Reports
// Nxt Era Solutions | Part of HES Consultancy International
// ============================================================================

// Supported languages with native names and flags
const languages = {
  nl: { name: 'Nederlands', native: 'Nederlands', flag: '🇳🇱', code: 'nl-NL', dir: 'ltr' },
  en: { name: 'English', native: 'English', flag: '🇬🇧', code: 'en-US', dir: 'ltr' },
  es: { name: 'Spanish', native: 'Español', flag: '🇪🇸', code: 'es-ES', dir: 'ltr' },
  fr: { name: 'French', native: 'Français', flag: '🇫🇷', code: 'fr-FR', dir: 'ltr' },
  ar: { name: 'Arabic', native: 'العربية', flag: '🇸🇦', code: 'ar-SA', dir: 'rtl' },
  fa: { name: 'Farsi', native: 'فارسی', flag: '🇮🇷', code: 'fa-IR', dir: 'rtl' },
  ti: { name: 'Tigrinya', native: 'ትግርኛ', flag: '🇪🇷', code: 'ti-ER', dir: 'ltr' },
  uk: { name: 'Ukrainian', native: 'Українська', flag: '🇺🇦', code: 'uk-UA', dir: 'ltr' },
  tr: { name: 'Turkish', native: 'Türkçe', flag: '🇹🇷', code: 'tr-TR', dir: 'ltr' },
  so: { name: 'Somali', native: 'Soomaali', flag: '🇸🇴', code: 'so-SO', dir: 'ltr' }
};

// Translations for UI and pre-set questions
const translations = {
  nl: {
    title: 'AI Welcome',
    subtitle: 'Welkom in Nederland',
    selectLanguage: 'Selecteer uw taal',
    quickQuestions: 'Veelgestelde vragen',
    askQuestion: 'Stel een vraag...',
    speak: 'Spreek',
    send: 'Verstuur',
    listening: 'Luistert...',
    translating: 'Vertaalt...',
    preRegistration: 'Pre-Registratie',
    postIntake: 'Intake Rapport',
    generateReport: 'Genereer Rapport',
    downloadReport: 'Download Rapport',
    close: 'Sluiten',
    staffView: 'Medewerker Weergave',
    clientView: 'Cliënt Weergave',
    original: 'Origineel',
    translated: 'Vertaald',
    questions: [
      { icon: '📋', text: 'Hoe vraag ik asiel aan?', category: 'procedure' },
      { icon: '🏠', text: 'Waar kan ik slapen?', category: 'housing' },
      { icon: '🍽️', text: 'Waar kan ik eten krijgen?', category: 'food' },
      { icon: '🏥', text: 'Ik heb medische hulp nodig', category: 'medical' },
      { icon: '👨‍👩‍👧', text: 'Ik reis met mijn gezin', category: 'family' },
      { icon: '📞', text: 'Hoe kan ik mijn familie bellen?', category: 'contact' },
      { icon: '⏱️', text: 'Hoe lang duurt de procedure?', category: 'timeline' },
      { icon: '📄', text: 'Welke documenten heb ik nodig?', category: 'documents' }
    ],
    responses: {
      procedure: 'U kunt asiel aanvragen bij de IND (Immigratie- en Naturalisatiedienst). Een medewerker zal u begeleiden door het registratieproces. Dit omvat identificatie, het afnemen van vingerafdrukken en een eerste gesprek.',
      housing: 'U wordt opgevangen in een asielzoekerscentrum (AZC). Eerst komt u in de centrale ontvangstlocatie in Ter Apel of Budel. Daarna wordt u geplaatst in een opvanglocatie elders in Nederland.',
      food: 'In het opvangcentrum krijgt u drie maaltijden per dag. Er is ook een wekelijkse vergoeding voor persoonlijke uitgaven. Speciale dieetwensen (halal, vegetarisch) worden gerespecteerd.',
      medical: 'Er is medische zorg beschikbaar. Een arts of verpleegkundige is aanwezig in het centrum. Voor spoedeisende hulp kunt u direct terecht. Geef alstublieft aan wat uw klachten zijn.',
      family: 'Gezinnen worden samen opgevangen. Er zijn speciale voorzieningen voor kinderen. Kinderen kunnen naar school zodra u in een vaste locatie verblijft.',
      contact: 'Er is wifi beschikbaar in het centrum. U kunt ook gebruik maken van de telefoons. Voor gezinshereniging zijn er speciale procedures beschikbaar.',
      timeline: 'De asielprocedure duurt gemiddeld 6-12 maanden. Tijdens deze periode verblijft u in de opvang. U krijgt regelmatig updates over uw zaak.',
      documents: 'Neem mee wat u heeft: paspoort, identiteitskaart, geboorteakte, diploma\'s. Als u geen documenten heeft, is dat geen probleem - we helpen u verder.'
    },
    reportLabels: {
      title: 'Intake Rapport',
      date: 'Datum',
      language: 'Voorkeurstaal',
      summary: 'Samenvatting Gesprek',
      topics: 'Besproken Onderwerpen',
      urgency: 'Urgentie',
      needs: 'Geïdentificeerde Behoeften',
      nextSteps: 'Aanbevolen Vervolgstappen',
      notes: 'Notities voor Medewerker',
      conversationLog: 'Gesprekslog'
    }
  },
  en: {
    title: 'AI Welcome',
    subtitle: 'Welcome to the Netherlands',
    selectLanguage: 'Select your language',
    quickQuestions: 'Common questions',
    askQuestion: 'Ask a question...',
    speak: 'Speak',
    send: 'Send',
    listening: 'Listening...',
    translating: 'Translating...',
    preRegistration: 'Pre-Registration',
    postIntake: 'Intake Report',
    generateReport: 'Generate Report',
    downloadReport: 'Download Report',
    close: 'Close',
    staffView: 'Staff View',
    clientView: 'Client View',
    original: 'Original',
    translated: 'Translated',
    questions: [
      { icon: '📋', text: 'How do I apply for asylum?', category: 'procedure' },
      { icon: '🏠', text: 'Where can I sleep?', category: 'housing' },
      { icon: '🍽️', text: 'Where can I get food?', category: 'food' },
      { icon: '🏥', text: 'I need medical help', category: 'medical' },
      { icon: '👨‍👩‍👧', text: 'I am traveling with my family', category: 'family' },
      { icon: '📞', text: 'How can I call my family?', category: 'contact' },
      { icon: '⏱️', text: 'How long is the procedure?', category: 'timeline' },
      { icon: '📄', text: 'What documents do I need?', category: 'documents' }
    ],
    responses: {
      procedure: 'You can apply for asylum at the IND (Immigration and Naturalisation Service). A staff member will guide you through the registration process. This includes identification, fingerprinting, and an initial interview.',
      housing: 'You will be housed in an asylum seekers center (AZC). First, you will go to the central reception location in Ter Apel or Budel. Then you will be placed in a reception facility elsewhere in the Netherlands.',
      food: 'You will receive three meals a day at the reception center. There is also a weekly allowance for personal expenses. Special dietary requirements (halal, vegetarian) are respected.',
      medical: 'Medical care is available. A doctor or nurse is present at the center. For emergencies, you can get help immediately. Please tell us what your symptoms are.',
      family: 'Families are housed together. There are special facilities for children. Children can attend school once you are in a permanent location.',
      contact: 'WiFi is available at the center. You can also use the phones. For family reunification, special procedures are available.',
      timeline: 'The asylum procedure takes an average of 6-12 months. During this period, you will stay in the reception center. You will receive regular updates about your case.',
      documents: 'Bring what you have: passport, ID card, birth certificate, diplomas. If you don\'t have documents, that\'s okay - we will help you further.'
    },
    reportLabels: {
      title: 'Intake Report',
      date: 'Date',
      language: 'Preferred Language',
      summary: 'Conversation Summary',
      topics: 'Topics Discussed',
      urgency: 'Urgency',
      needs: 'Identified Needs',
      nextSteps: 'Recommended Next Steps',
      notes: 'Notes for Staff',
      conversationLog: 'Conversation Log'
    }
  },
  es: {
    title: 'AI Welcome',
    subtitle: 'Bienvenido a los Países Bajos',
    selectLanguage: 'Seleccione su idioma',
    quickQuestions: 'Preguntas frecuentes',
    askQuestion: 'Haga una pregunta...',
    speak: 'Hablar',
    send: 'Enviar',
    listening: 'Escuchando...',
    translating: 'Traduciendo...',
    preRegistration: 'Pre-Registro',
    postIntake: 'Informe de Admisión',
    generateReport: 'Generar Informe',
    downloadReport: 'Descargar Informe',
    close: 'Cerrar',
    staffView: 'Vista del Personal',
    clientView: 'Vista del Cliente',
    original: 'Original',
    translated: 'Traducido',
    questions: [
      { icon: '📋', text: '¿Cómo solicito asilo?', category: 'procedure' },
      { icon: '🏠', text: '¿Dónde puedo dormir?', category: 'housing' },
      { icon: '🍽️', text: '¿Dónde puedo conseguir comida?', category: 'food' },
      { icon: '🏥', text: 'Necesito ayuda médica', category: 'medical' },
      { icon: '👨‍👩‍👧', text: 'Viajo con mi familia', category: 'family' },
      { icon: '📞', text: '¿Cómo puedo llamar a mi familia?', category: 'contact' },
      { icon: '⏱️', text: '¿Cuánto dura el procedimiento?', category: 'timeline' },
      { icon: '📄', text: '¿Qué documentos necesito?', category: 'documents' }
    ],
    responses: {
      procedure: 'Puede solicitar asilo en el IND (Servicio de Inmigración y Naturalización). Un empleado le guiará a través del proceso de registro. Esto incluye identificación, huellas dactilares y una entrevista inicial.',
      housing: 'Será alojado en un centro de solicitantes de asilo (AZC). Primero, irá a la ubicación de recepción central en Ter Apel o Budel. Luego será ubicado en otra instalación en los Países Bajos.',
      food: 'Recibirá tres comidas al día en el centro de recepción. También hay una asignación semanal para gastos personales. Se respetan los requisitos dietéticos especiales (halal, vegetariano).',
      medical: 'Hay atención médica disponible. Un médico o enfermera está presente en el centro. Para emergencias, puede obtener ayuda inmediatamente. Por favor, díganos cuáles son sus síntomas.',
      family: 'Las familias se alojan juntas. Hay instalaciones especiales para niños. Los niños pueden asistir a la escuela una vez que esté en una ubicación permanente.',
      contact: 'Hay WiFi disponible en el centro. También puede usar los teléfonos. Para la reunificación familiar, hay procedimientos especiales disponibles.',
      timeline: 'El procedimiento de asilo toma un promedio de 6-12 meses. Durante este período, permanecerá en el centro de recepción. Recibirá actualizaciones regulares sobre su caso.',
      documents: 'Traiga lo que tenga: pasaporte, tarjeta de identificación, certificado de nacimiento, diplomas. Si no tiene documentos, está bien - le ayudaremos.'
    },
    reportLabels: {
      title: 'Informe de Admisión',
      date: 'Fecha',
      language: 'Idioma Preferido',
      summary: 'Resumen de la Conversación',
      topics: 'Temas Discutidos',
      urgency: 'Urgencia',
      needs: 'Necesidades Identificadas',
      nextSteps: 'Próximos Pasos Recomendados',
      notes: 'Notas para el Personal',
      conversationLog: 'Registro de Conversación'
    }
  },
  fr: {
    title: 'AI Welcome',
    subtitle: 'Bienvenue aux Pays-Bas',
    selectLanguage: 'Sélectionnez votre langue',
    quickQuestions: 'Questions fréquentes',
    askQuestion: 'Posez une question...',
    speak: 'Parler',
    send: 'Envoyer',
    listening: 'Écoute...',
    translating: 'Traduction...',
    preRegistration: 'Pré-inscription',
    postIntake: 'Rapport d\'admission',
    generateReport: 'Générer le Rapport',
    downloadReport: 'Télécharger le Rapport',
    close: 'Fermer',
    staffView: 'Vue du Personnel',
    clientView: 'Vue du Client',
    original: 'Original',
    translated: 'Traduit',
    questions: [
      { icon: '📋', text: 'Comment demander l\'asile?', category: 'procedure' },
      { icon: '🏠', text: 'Où puis-je dormir?', category: 'housing' },
      { icon: '🍽️', text: 'Où puis-je manger?', category: 'food' },
      { icon: '🏥', text: 'J\'ai besoin d\'aide médicale', category: 'medical' },
      { icon: '👨‍👩‍👧', text: 'Je voyage avec ma famille', category: 'family' },
      { icon: '📞', text: 'Comment appeler ma famille?', category: 'contact' },
      { icon: '⏱️', text: 'Combien de temps dure la procédure?', category: 'timeline' },
      { icon: '📄', text: 'Quels documents dois-je avoir?', category: 'documents' }
    ],
    responses: {
      procedure: 'Vous pouvez demander l\'asile auprès de l\'IND (Service d\'Immigration et de Naturalisation). Un employé vous guidera dans le processus d\'enregistrement. Cela comprend l\'identification, les empreintes digitales et un premier entretien.',
      housing: 'Vous serez hébergé dans un centre pour demandeurs d\'asile (AZC). D\'abord, vous irez au centre d\'accueil de Ter Apel ou Budel. Ensuite, vous serez placé dans un autre établissement aux Pays-Bas.',
      food: 'Vous recevrez trois repas par jour au centre d\'accueil. Il y a aussi une allocation hebdomadaire pour les dépenses personnelles. Les régimes spéciaux (halal, végétarien) sont respectés.',
      medical: 'Des soins médicaux sont disponibles. Un médecin ou une infirmière est présent au centre. Pour les urgences, vous pouvez obtenir de l\'aide immédiatement. Veuillez nous dire quels sont vos symptômes.',
      family: 'Les familles sont logées ensemble. Il y a des installations spéciales pour les enfants. Les enfants peuvent aller à l\'école une fois que vous êtes dans un lieu permanent.',
      contact: 'Le WiFi est disponible au centre. Vous pouvez également utiliser les téléphones. Pour le regroupement familial, des procédures spéciales sont disponibles.',
      timeline: 'La procédure d\'asile prend en moyenne 6 à 12 mois. Pendant cette période, vous resterez au centre d\'accueil. Vous recevrez des mises à jour régulières sur votre dossier.',
      documents: 'Apportez ce que vous avez: passeport, carte d\'identité, acte de naissance, diplômes. Si vous n\'avez pas de documents, ce n\'est pas grave - nous vous aiderons.'
    },
    reportLabels: {
      title: 'Rapport d\'Admission',
      date: 'Date',
      language: 'Langue Préférée',
      summary: 'Résumé de la Conversation',
      topics: 'Sujets Discutés',
      urgency: 'Urgence',
      needs: 'Besoins Identifiés',
      nextSteps: 'Prochaines Étapes Recommandées',
      notes: 'Notes pour le Personnel',
      conversationLog: 'Journal de Conversation'
    }
  },
  ar: {
    title: 'AI Welcome',
    subtitle: 'مرحباً بكم في هولندا',
    selectLanguage: 'اختر لغتك',
    quickQuestions: 'أسئلة شائعة',
    askQuestion: 'اطرح سؤالاً...',
    speak: 'تحدث',
    send: 'إرسال',
    listening: 'جاري الاستماع...',
    translating: 'جاري الترجمة...',
    preRegistration: 'التسجيل المسبق',
    postIntake: 'تقرير القبول',
    generateReport: 'إنشاء التقرير',
    downloadReport: 'تحميل التقرير',
    close: 'إغلاق',
    staffView: 'عرض الموظف',
    clientView: 'عرض العميل',
    original: 'الأصلي',
    translated: 'مترجم',
    questions: [
      { icon: '📋', text: 'كيف أتقدم بطلب اللجوء؟', category: 'procedure' },
      { icon: '🏠', text: 'أين يمكنني النوم؟', category: 'housing' },
      { icon: '🍽️', text: 'أين يمكنني الحصول على الطعام؟', category: 'food' },
      { icon: '🏥', text: 'أحتاج إلى مساعدة طبية', category: 'medical' },
      { icon: '👨‍👩‍👧', text: 'أنا مسافر مع عائلتي', category: 'family' },
      { icon: '📞', text: 'كيف يمكنني الاتصال بعائلتي؟', category: 'contact' },
      { icon: '⏱️', text: 'كم تستغرق الإجراءات؟', category: 'timeline' },
      { icon: '📄', text: 'ما هي الوثائق المطلوبة؟', category: 'documents' }
    ],
    responses: {
      procedure: 'يمكنك التقدم بطلب اللجوء في IND (دائرة الهجرة والتجنس). سيرشدك أحد الموظفين خلال عملية التسجيل. يشمل ذلك التعريف بالهوية وبصمات الأصابع ومقابلة أولية.',
      housing: 'ستتم استضافتك في مركز لطالبي اللجوء (AZC). أولاً، ستذهب إلى موقع الاستقبال المركزي في تير أبيل أو بوديل. ثم سيتم وضعك في مرفق استقبال آخر في هولندا.',
      food: 'ستحصل على ثلاث وجبات يومياً في مركز الاستقبال. هناك أيضاً بدل أسبوعي للنفقات الشخصية. يتم احترام المتطلبات الغذائية الخاصة (حلال، نباتي).',
      medical: 'الرعاية الطبية متاحة. يوجد طبيب أو ممرضة في المركز. للطوارئ، يمكنك الحصول على المساعدة فوراً. يرجى إخبارنا بأعراضك.',
      family: 'يتم إيواء العائلات معاً. هناك مرافق خاصة للأطفال. يمكن للأطفال الذهاب إلى المدرسة بمجرد أن تكون في موقع دائم.',
      contact: 'تتوفر خدمة الواي فاي في المركز. يمكنك أيضاً استخدام الهواتف. لم الشمل العائلي، تتوفر إجراءات خاصة.',
      timeline: 'تستغرق إجراءات اللجوء في المتوسط 6-12 شهراً. خلال هذه الفترة، ستبقى في مركز الاستقبال. ستتلقى تحديثات منتظمة حول قضيتك.',
      documents: 'أحضر ما لديك: جواز السفر، بطاقة الهوية، شهادة الميلاد، الشهادات. إذا لم تكن لديك وثائق، فلا بأس - سنساعدك.'
    },
    reportLabels: {
      title: 'تقرير القبول',
      date: 'التاريخ',
      language: 'اللغة المفضلة',
      summary: 'ملخص المحادثة',
      topics: 'المواضيع المناقشة',
      urgency: 'الاستعجال',
      needs: 'الاحتياجات المحددة',
      nextSteps: 'الخطوات التالية الموصى بها',
      notes: 'ملاحظات للموظفين',
      conversationLog: 'سجل المحادثة'
    }
  },
  fa: {
    title: 'AI Welcome',
    subtitle: 'به هلند خوش آمدید',
    selectLanguage: 'زبان خود را انتخاب کنید',
    quickQuestions: 'سوالات متداول',
    askQuestion: 'سوال بپرسید...',
    speak: 'صحبت کنید',
    send: 'ارسال',
    listening: 'در حال گوش دادن...',
    translating: 'در حال ترجمه...',
    preRegistration: 'پیش ثبت نام',
    postIntake: 'گزارش پذیرش',
    generateReport: 'ایجاد گزارش',
    downloadReport: 'دانلود گزارش',
    close: 'بستن',
    staffView: 'نمای کارمند',
    clientView: 'نمای مراجع',
    original: 'اصلی',
    translated: 'ترجمه شده',
    questions: [
      { icon: '📋', text: 'چگونه درخواست پناهندگی دهم؟', category: 'procedure' },
      { icon: '🏠', text: 'کجا می توانم بخوابم؟', category: 'housing' },
      { icon: '🍽️', text: 'کجا می توانم غذا بگیرم؟', category: 'food' },
      { icon: '🏥', text: 'به کمک پزشکی نیاز دارم', category: 'medical' },
      { icon: '👨‍👩‍👧', text: 'با خانواده ام سفر می کنم', category: 'family' },
      { icon: '📞', text: 'چگونه با خانواده ام تماس بگیرم؟', category: 'contact' },
      { icon: '⏱️', text: 'روند پناهندگی چقدر طول می کشد؟', category: 'timeline' },
      { icon: '📄', text: 'چه مدارکی نیاز دارم؟', category: 'documents' }
    ],
    responses: {
      procedure: 'می توانید در IND (اداره مهاجرت و تابعیت) درخواست پناهندگی دهید. یک کارمند شما را در فرآیند ثبت نام راهنمایی می کند. این شامل شناسایی، انگشت نگاری و مصاحبه اولیه است.',
      housing: 'شما در مرکز پناهجویان (AZC) اسکان داده می شوید. ابتدا به محل پذیرش مرکزی در تر آپل یا بودل می روید. سپس در مرکز پذیرش دیگری در هلند قرار می گیرید.',
      food: 'روزانه سه وعده غذا در مرکز پذیرش دریافت می کنید. همچنین کمک هزینه هفتگی برای مخارج شخصی وجود دارد. نیازهای غذایی خاص (حلال، گیاهخواری) رعایت می شود.',
      medical: 'مراقبت پزشکی در دسترس است. پزشک یا پرستار در مرکز حضور دارد. برای موارد اضطراری، می توانید فوراً کمک بگیرید. لطفاً علائم خود را به ما بگویید.',
      family: 'خانواده ها با هم اسکان داده می شوند. امکانات ویژه ای برای کودکان وجود دارد. کودکان می توانند پس از استقرار در محل دائمی به مدرسه بروند.',
      contact: 'وای فای در مرکز موجود است. همچنین می توانید از تلفن ها استفاده کنید. برای الحاق خانواده، روش های خاصی در دسترس است.',
      timeline: 'روند پناهندگی به طور متوسط 6 تا 12 ماه طول می کشد. در این مدت در مرکز پذیرش می مانید. به طور منظم از وضعیت پرونده خود مطلع می شوید.',
      documents: 'هر چه دارید بیاورید: گذرنامه، کارت شناسایی، شناسنامه، مدارک تحصیلی. اگر مدرکی ندارید، مشکلی نیست - ما به شما کمک می کنیم.'
    },
    reportLabels: {
      title: 'گزارش پذیرش',
      date: 'تاریخ',
      language: 'زبان ترجیحی',
      summary: 'خلاصه گفتگو',
      topics: 'موضوعات مورد بحث',
      urgency: 'فوریت',
      needs: 'نیازهای شناسایی شده',
      nextSteps: 'مراحل بعدی توصیه شده',
      notes: 'یادداشت برای کارمندان',
      conversationLog: 'گزارش مکالمه'
    }
  },
  ti: {
    title: 'AI Welcome',
    subtitle: 'ናብ ኔዘርላንድ እንቋዕ ብደሓን መጻእኩም',
    selectLanguage: 'ቋንቋኹም ምረጹ',
    quickQuestions: 'ልሙዳት ሕቶታት',
    askQuestion: 'ሕቶ ሕተት...',
    speak: 'ተዛረብ',
    send: 'ስደድ',
    listening: 'ይሰምዕ ኣሎ...',
    translating: 'ይትርጉም ኣሎ...',
    preRegistration: 'ቅድመ ምዝገባ',
    postIntake: 'ጸብጻብ ምቕባል',
    generateReport: 'ጸብጻብ ፍጠር',
    downloadReport: 'ጸብጻብ ኣውርድ',
    close: 'ዕጾ',
    staffView: 'ርእየት ሰራሕተኛ',
    clientView: 'ርእየት ደንበኛ',
    original: 'መበቆላዊ',
    translated: 'ዝተተርጎመ',
    questions: [
      { icon: '📋', text: 'ከመይ ገይረ ዑቕባ ይሓትት?', category: 'procedure' },
      { icon: '🏠', text: 'ኣበይ ክድቅስ ይኽእል?', category: 'housing' },
      { icon: '🍽️', text: 'ኣበይ ምግቢ ክረክብ ይኽእል?', category: 'food' },
      { icon: '🏥', text: 'ሕክምናዊ ሓገዝ የድልየኒ', category: 'medical' },
      { icon: '👨‍👩‍👧', text: 'ምስ ስድራይ እጎዓዝ ኣለኹ', category: 'family' },
      { icon: '📞', text: 'ከመይ ገይረ ንስድራይ ክድውል?', category: 'contact' },
      { icon: '⏱️', text: 'እቲ መስርሕ ክንደይ ግዜ ይወስድ?', category: 'timeline' },
      { icon: '📄', text: 'እንታይ ሰነዳት የድልየኒ?', category: 'documents' }
    ],
    responses: {
      procedure: 'ኣብ IND ዑቕባ ክትሓትት ትኽእል። ሰራሕተኛ ናይ ምዝገባ መስርሕ ክመርሓካ እዩ።',
      housing: 'ኣብ ማእከል ሓተትቲ ዑቕባ (AZC) ክትቕመጥ ኢኻ።',
      food: 'ኣብ መዓልቲ ሰለስተ ግዜ ምግቢ ክትረክብ ኢኻ።',
      medical: 'ሕክምናዊ ኣገልግሎት ኣሎ። ሓኪም ወይ ነርስ ኣብቲ ማእከል ኣሎ።',
      family: 'ስድራቤታት ብሓባር ይቕመጡ።',
      contact: 'ዋይፋይ ኣሎ። ተሌፎን እውን ክትጥቀም ትኽእል።',
      timeline: 'እቲ መስርሕ ብማእከላይ 6-12 ኣዋርሕ ይወስድ።',
      documents: 'ዘለካ ኣምጽእ: ፓስፖርት, መንነት, ምስክር ልደት።'
    },
    reportLabels: {
      title: 'ጸብጻብ ምቕባል',
      date: 'ዕለት',
      language: 'ዝመረጽካዮ ቋንቋ',
      summary: 'ጽማቕ ዝርርብ',
      topics: 'ዝተዘራረብናሉ ኣርእስትታት',
      urgency: 'ህጹጽነት',
      needs: 'ዝተለለዩ ድሌታት',
      nextSteps: 'ዝስዕቡ ስጉምትታት',
      notes: 'መዘከርታ ንሰራሕተኛ',
      conversationLog: 'መዝገብ ዝርርብ'
    }
  },
  uk: {
    title: 'AI Welcome',
    subtitle: 'Ласкаво просимо до Нідерландів',
    selectLanguage: 'Оберіть мову',
    quickQuestions: 'Часті запитання',
    askQuestion: 'Задайте питання...',
    speak: 'Говорити',
    send: 'Надіслати',
    listening: 'Слухаю...',
    translating: 'Перекладаю...',
    preRegistration: 'Попередня реєстрація',
    postIntake: 'Звіт про прийом',
    generateReport: 'Створити звіт',
    downloadReport: 'Завантажити звіт',
    close: 'Закрити',
    staffView: 'Вигляд для персоналу',
    clientView: 'Вигляд для клієнта',
    original: 'Оригінал',
    translated: 'Переклад',
    questions: [
      { icon: '📋', text: 'Як подати заяву на притулок?', category: 'procedure' },
      { icon: '🏠', text: 'Де я можу переночувати?', category: 'housing' },
      { icon: '🍽️', text: 'Де я можу отримати їжу?', category: 'food' },
      { icon: '🏥', text: 'Мені потрібна медична допомога', category: 'medical' },
      { icon: '👨‍👩‍👧', text: 'Я подорожую з сім\'єю', category: 'family' },
      { icon: '📞', text: 'Як зателефонувати родині?', category: 'contact' },
      { icon: '⏱️', text: 'Скільки триває процедура?', category: 'timeline' },
      { icon: '📄', text: 'Які документи потрібні?', category: 'documents' }
    ],
    responses: {
      procedure: 'Ви можете подати заяву на притулок в IND (Імміграційна та Натуралізаційна Служба). Співробітник проведе вас через процес реєстрації.',
      housing: 'Вас розмістять у центрі прийому біженців (AZC). Спочатку ви потрапите до центрального приймального пункту.',
      food: 'Ви отримуватимете триразове харчування. Також є щотижнева допомога на особисті витрати.',
      medical: 'Медична допомога доступна. У центрі є лікар або медсестра.',
      family: 'Сім\'ї розміщуються разом. Є спеціальні умови для дітей.',
      contact: 'У центрі є WiFi. Ви також можете користуватися телефонами.',
      timeline: 'Процедура надання притулку займає в середньому 6-12 місяців.',
      documents: 'Принесіть те, що маєте: паспорт, посвідчення особи, свідоцтво про народження.'
    },
    reportLabels: {
      title: 'Звіт про прийом',
      date: 'Дата',
      language: 'Бажана мова',
      summary: 'Резюме розмови',
      topics: 'Обговорені теми',
      urgency: 'Терміновість',
      needs: 'Виявлені потреби',
      nextSteps: 'Рекомендовані наступні кроки',
      notes: 'Нотатки для персоналу',
      conversationLog: 'Журнал розмови'
    }
  },
  tr: {
    title: 'AI Welcome',
    subtitle: 'Hollanda\'ya Hoş Geldiniz',
    selectLanguage: 'Dilinizi seçin',
    quickQuestions: 'Sık sorulan sorular',
    askQuestion: 'Soru sorun...',
    speak: 'Konuş',
    send: 'Gönder',
    listening: 'Dinliyor...',
    translating: 'Çeviriyor...',
    preRegistration: 'Ön Kayıt',
    postIntake: 'Kabul Raporu',
    generateReport: 'Rapor Oluştur',
    downloadReport: 'Raporu İndir',
    close: 'Kapat',
    staffView: 'Personel Görünümü',
    clientView: 'Müşteri Görünümü',
    original: 'Orijinal',
    translated: 'Çevrilmiş',
    questions: [
      { icon: '📋', text: 'Nasıl sığınma başvurusu yapabilirim?', category: 'procedure' },
      { icon: '🏠', text: 'Nerede kalabilirim?', category: 'housing' },
      { icon: '🍽️', text: 'Nereden yemek alabilirim?', category: 'food' },
      { icon: '🏥', text: 'Tıbbi yardıma ihtiyacım var', category: 'medical' },
      { icon: '👨‍👩‍👧', text: 'Ailemle seyahat ediyorum', category: 'family' },
      { icon: '📞', text: 'Ailemi nasıl arayabilirim?', category: 'contact' },
      { icon: '⏱️', text: 'Süreç ne kadar sürer?', category: 'timeline' },
      { icon: '📄', text: 'Hangi belgeler gerekli?', category: 'documents' }
    ],
    responses: {
      procedure: 'IND\'de sığınma başvurusu yapabilirsiniz. Bir görevli kayıt sürecinde size rehberlik edecektir.',
      housing: 'Sığınmacı merkezinde (AZC) kalacaksınız. Önce merkezi kabul noktasına gideceksiniz.',
      food: 'Günde üç öğün yemek alacaksınız. Kişisel harcamalar için haftalık ödenek de var.',
      medical: 'Tıbbi bakım mevcuttur. Merkezde doktor veya hemşire bulunmaktadır.',
      family: 'Aileler birlikte kalır. Çocuklar için özel imkanlar vardır.',
      contact: 'Merkezde WiFi mevcuttur. Telefonları da kullanabilirsiniz.',
      timeline: 'Sığınma süreci ortalama 6-12 ay sürer.',
      documents: 'Elinizde olanı getirin: pasaport, kimlik, doğum belgesi.'
    },
    reportLabels: {
      title: 'Kabul Raporu',
      date: 'Tarih',
      language: 'Tercih Edilen Dil',
      summary: 'Görüşme Özeti',
      topics: 'Tartışılan Konular',
      urgency: 'Aciliyet',
      needs: 'Belirlenen İhtiyaçlar',
      nextSteps: 'Önerilen Sonraki Adımlar',
      notes: 'Personel Notları',
      conversationLog: 'Görüşme Kaydı'
    }
  },
  so: {
    title: 'AI Welcome',
    subtitle: 'Ku soo dhawoow Netherlands',
    selectLanguage: 'Dooro luqaddaada',
    quickQuestions: 'Su\'aalaha caadiga ah',
    askQuestion: 'Weydii su\'aal...',
    speak: 'Hadal',
    send: 'Dir',
    listening: 'Dhagaysanaya...',
    translating: 'Tarjumaya...',
    preRegistration: 'Diiwaangelin hore',
    postIntake: 'Warbixinta qaabilaadda',
    generateReport: 'Samee Warbixin',
    downloadReport: 'Soo degso Warbixin',
    close: 'Xir',
    staffView: 'Muuqaalka Shaqaalaha',
    clientView: 'Muuqaalka Macmiilka',
    original: 'Asalka',
    translated: 'La turjumay',
    questions: [
      { icon: '📋', text: 'Sideen u codsadaa magangelyo?', category: 'procedure' },
      { icon: '🏠', text: 'Halkee seexan karaa?', category: 'housing' },
      { icon: '🍽️', text: 'Halkee cunto ka heli karaa?', category: 'food' },
      { icon: '🏥', text: 'Waxaan u baahanahay caawimo caafimaad', category: 'medical' },
      { icon: '👨‍👩‍👧', text: 'Waxaan la socdaa qoyskayga', category: 'family' },
      { icon: '📞', text: 'Sideen ugu wici karaa qoyskayga?', category: 'contact' },
      { icon: '⏱️', text: 'Nidaamku intee leeg yahay?', category: 'timeline' },
      { icon: '📄', text: 'Dukumentiyee baan u baahanahay?', category: 'documents' }
    ],
    responses: {
      procedure: 'Waxaad magangelyo ka codsan kartaa IND. Shaqaale ayaa ku hagi doona nidaamka diiwaangelinta.',
      housing: 'Waxaa lagugu dejin doonaa xarunta magangelyodoonka (AZC).',
      food: 'Maalintii saddex jeer ayaad cunto heli doontaa.',
      medical: 'Daryeel caafimaad waa la heli karaa. Dhakhtar ama kalkaaliso ayaa xarunta jooga.',
      family: 'Qoysaska waa la wada dejiyaa. Waxaa jira xarumaha gaarka ah ee carruurta.',
      contact: 'WiFi waa la heli karaa xarunta. Taleefanada ayaad isticmaali kartaa.',
      timeline: 'Nidaamka magangelyada wuxuu qaadanayaa celcelis ahaan 6-12 bilood.',
      documents: 'Keen wixii aad haysato: baasaboor, kaarka aqoonsiga, shahaadada dhalashada.'
    },
    reportLabels: {
      title: 'Warbixinta Qaabilaadda',
      date: 'Taariikhda',
      language: 'Luqadda La Doorbidey',
      summary: 'Soo koobida Wada hadalka',
      topics: 'Mawduucyada La Doodey',
      urgency: 'Degdegga',
      needs: 'Baahiyaha La Aqoonsaday',
      nextSteps: 'Talaabooyinka Xiga ee La Soo Jeediyay',
      notes: 'Qoraallada Shaqaalaha',
      conversationLog: 'Diiwaanka Wada hadalka'
    }
  }
};

// Styles
const styles = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: linear-gradient(135deg, #0D1B2A 0%, #1E3A5F 50%, #2E86AB 100%);
    min-height: 100vh;
  }

  .app-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    color: #fff;
  }

  /* Header */
  .header {
    padding: 15px 25px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: rgba(0,0,0,0.3);
    backdrop-filter: blur(10px);
  }

  .logo-section {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .logo-icon {
    width: 45px;
    height: 45px;
    background: linear-gradient(135deg, #27AE60, #2ECC71);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
  }

  .logo-text h1 {
    font-size: 1.3rem;
    font-weight: 700;
  }

  .logo-text p {
    font-size: 0.8rem;
    opacity: 0.7;
  }

  .header-controls {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .view-toggle {
    display: flex;
    background: rgba(255,255,255,0.1);
    border-radius: 20px;
    padding: 3px;
  }

  .view-btn {
    padding: 8px 15px;
    border: none;
    background: transparent;
    color: rgba(255,255,255,0.7);
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 500;
    border-radius: 17px;
    transition: all 0.3s;
  }

  .view-btn.active {
    background: #27AE60;
    color: white;
  }

  /* Language Selection Screen */
  .language-select-screen {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 30px;
    text-align: center;
  }

  .welcome-icon {
    font-size: 5rem;
    margin-bottom: 20px;
  }

  .welcome-title {
    font-size: 2.5rem;
    margin-bottom: 10px;
    background: linear-gradient(135deg, #fff, #27AE60);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .welcome-subtitle {
    font-size: 1.2rem;
    opacity: 0.8;
    margin-bottom: 40px;
  }

  .language-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 15px;
    max-width: 700px;
    width: 100%;
  }

  .language-card {
    background: rgba(255,255,255,0.1);
    backdrop-filter: blur(10px);
    border: 2px solid rgba(255,255,255,0.15);
    border-radius: 16px;
    padding: 20px 15px;
    cursor: pointer;
    transition: all 0.3s;
    text-align: center;
  }

  .language-card:hover {
    transform: translateY(-5px);
    background: rgba(255,255,255,0.15);
    border-color: #27AE60;
  }

  .language-card.selected {
    border-color: #27AE60;
    background: rgba(39,174,96,0.2);
  }

  .language-flag {
    font-size: 2.5rem;
    margin-bottom: 10px;
  }

  .language-native {
    font-size: 1rem;
    font-weight: 600;
  }

  .language-name {
    font-size: 0.8rem;
    opacity: 0.7;
    margin-top: 3px;
  }

  /* Main Chat Interface */
  .main-content {
    flex: 1;
    display: flex;
    padding: 20px;
    gap: 20px;
    max-width: 1400px;
    margin: 0 auto;
    width: 100%;
  }

  .chat-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: rgba(255,255,255,0.05);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    border: 1px solid rgba(255,255,255,0.1);
    overflow: hidden;
  }

  /* Quick Questions */
  .quick-questions {
    padding: 15px 20px;
    background: rgba(0,0,0,0.2);
    border-bottom: 1px solid rgba(255,255,255,0.1);
  }

  .quick-questions h3 {
    font-size: 0.85rem;
    opacity: 0.7;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .questions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
  }

  .question-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 15px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 12px;
    color: white;
    cursor: pointer;
    font-size: 0.9rem;
    text-align: left;
    transition: all 0.3s;
  }

  .question-btn:hover {
    background: rgba(39,174,96,0.2);
    border-color: #27AE60;
    transform: translateX(5px);
  }

  .question-icon {
    font-size: 1.3rem;
    flex-shrink: 0;
  }

  /* Chat Messages */
  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .message {
    display: flex;
    gap: 12px;
    max-width: 85%;
    animation: slideIn 0.3s ease;
  }

  @keyframes slideIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .message.user {
    align-self: flex-end;
    flex-direction: row-reverse;
  }

  .message.rtl {
    direction: rtl;
  }

  .message-avatar {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  .message.ai .message-avatar {
    background: linear-gradient(135deg, #27AE60, #2ECC71);
  }

  .message.user .message-avatar {
    background: linear-gradient(135deg, #E86C3A, #FF8C5A);
  }

  .message-bubble {
    background: rgba(255,255,255,0.1);
    border-radius: 18px;
    padding: 15px 18px;
    border: 1px solid rgba(255,255,255,0.1);
  }

  .message.user .message-bubble {
    background: linear-gradient(135deg, #E86C3A, #FF8C5A);
    border: none;
  }

  .message-text {
    line-height: 1.6;
  }

  .message-translation {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(255,255,255,0.2);
    font-size: 0.9rem;
    opacity: 0.85;
  }

  .translation-label {
    font-size: 0.75rem;
    opacity: 0.6;
    margin-bottom: 5px;
    text-transform: uppercase;
  }

  .typing-indicator {
    display: flex;
    gap: 5px;
    padding: 10px;
  }

  .typing-indicator span {
    width: 8px;
    height: 8px;
    background: rgba(255,255,255,0.5);
    border-radius: 50%;
    animation: bounce 1.4s infinite;
  }

  .typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
  .typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

  @keyframes bounce {
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-8px); }
  }

  /* Chat Input */
  .chat-input-area {
    padding: 15px 20px;
    background: rgba(0,0,0,0.2);
    border-top: 1px solid rgba(255,255,255,0.1);
  }

  .input-wrapper {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .chat-input {
    flex: 1;
    padding: 14px 18px;
    background: rgba(255,255,255,0.1);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 14px;
    color: white;
    font-size: 1rem;
    outline: none;
    transition: all 0.3s;
  }

  .chat-input:focus {
    border-color: #27AE60;
    background: rgba(255,255,255,0.15);
  }

  .chat-input::placeholder {
    color: rgba(255,255,255,0.5);
  }

  .input-btn {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    border: none;
    cursor: pointer;
    font-size: 1.2rem;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .voice-btn {
    background: rgba(255,255,255,0.1);
    color: white;
    border: 1px solid rgba(255,255,255,0.2);
  }

  .voice-btn:hover {
    background: rgba(255,255,255,0.2);
  }

  .voice-btn.listening {
    background: #E53E3E;
    animation: pulse 1.5s infinite;
    border: none;
  }

  @keyframes pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.5); }
    50% { box-shadow: 0 0 0 12px rgba(229, 62, 62, 0); }
  }

  .send-btn {
    background: linear-gradient(135deg, #27AE60, #2ECC71);
    color: white;
  }

  .send-btn:hover {
    transform: scale(1.05);
  }

  .send-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  /* Sidebar */
  .sidebar {
    width: 300px;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .sidebar-card {
    background: rgba(255,255,255,0.05);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 20px;
  }

  .sidebar-card h4 {
    font-size: 0.85rem;
    opacity: 0.7;
    margin-bottom: 15px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  /* Language Info */
  .current-language {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .lang-flag-large {
    font-size: 2.5rem;
  }

  .lang-details h5 {
    font-size: 1.1rem;
    margin-bottom: 3px;
  }

  .lang-details p {
    font-size: 0.85rem;
    opacity: 0.7;
  }

  .change-lang-btn {
    margin-top: 15px;
    width: 100%;
    padding: 10px;
    background: rgba(255,255,255,0.1);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 10px;
    color: white;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.3s;
  }

  .change-lang-btn:hover {
    background: rgba(255,255,255,0.2);
  }

  /* Topics */
  .topics-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .topic-tag {
    padding: 6px 12px;
    background: rgba(39,174,96,0.2);
    border: 1px solid rgba(39,174,96,0.3);
    border-radius: 20px;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  /* Report Buttons */
  .report-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .report-btn {
    padding: 14px;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    font-size: 0.95rem;
    font-weight: 600;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .report-btn.pre {
    background: rgba(46,134,171,0.3);
    color: white;
    border: 1px solid rgba(46,134,171,0.5);
  }

  .report-btn.post {
    background: linear-gradient(135deg, #27AE60, #2ECC71);
    color: white;
  }

  .report-btn:hover {
    transform: translateY(-2px);
  }

  /* Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }

  .modal-content {
    background: linear-gradient(135deg, #1E3A5F, #2E86AB);
    border-radius: 20px;
    max-width: 700px;
    width: 100%;
    max-height: 85vh;
    overflow-y: auto;
    border: 1px solid rgba(255,255,255,0.2);
  }

  .modal-header {
    padding: 20px 25px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    background: inherit;
    z-index: 10;
  }

  .modal-header h3 {
    font-size: 1.3rem;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .modal-close {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid rgba(255,255,255,0.2);
    background: transparent;
    color: white;
    cursor: pointer;
    font-size: 1.1rem;
    transition: all 0.3s;
  }

  .modal-close:hover {
    background: rgba(255,255,255,0.1);
  }

  .modal-body {
    padding: 25px;
  }

  .report-section {
    margin-bottom: 25px;
  }

  .report-section h4 {
    font-size: 0.85rem;
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 10px;
  }

  .report-section p,
  .report-section ul {
    color: #fff;
    line-height: 1.7;
  }

  .report-section ul {
    list-style: none;
    padding: 0;
  }

  .report-section li {
    padding: 8px 0;
    border-bottom: 1px solid rgba(255,255,255,0.1);
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .report-section li:last-child {
    border-bottom: none;
  }

  .urgency-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 20px;
    font-weight: 600;
  }

  .urgency-badge.low {
    background: rgba(39,174,96,0.2);
    color: #2ECC71;
  }

  .urgency-badge.medium {
    background: rgba(243,156,18,0.2);
    color: #F39C12;
  }

  .urgency-badge.high {
    background: rgba(231,76,60,0.2);
    color: #E74C3C;
  }

  .conversation-log {
    background: rgba(0,0,0,0.2);
    border-radius: 12px;
    padding: 15px;
    max-height: 300px;
    overflow-y: auto;
  }

  .log-entry {
    padding: 10px 0;
    border-bottom: 1px solid rgba(255,255,255,0.1);
    font-size: 0.9rem;
  }

  .log-entry:last-child {
    border-bottom: none;
  }

  .log-speaker {
    font-weight: 600;
    margin-right: 8px;
  }

  .log-speaker.user { color: #E86C3A; }
  .log-speaker.ai { color: #2ECC71; }

  .modal-footer {
    padding: 20px 25px;
    border-top: 1px solid rgba(255,255,255,0.1);
    display: flex;
    gap: 15px;
  }

  .modal-btn {
    flex: 1;
    padding: 14px;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
  }

  .modal-btn.primary {
    background: linear-gradient(135deg, #27AE60, #2ECC71);
    border: none;
    color: white;
  }

  .modal-btn.secondary {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.3);
    color: white;
  }

  /* Footer */
  .footer {
    padding: 15px 25px;
    text-align: center;
    background: rgba(0,0,0,0.3);
    color: rgba(255,255,255,0.6);
    font-size: 0.8rem;
  }

  /* Responsive */
  @media (max-width: 1024px) {
    .main-content {
      flex-direction: column;
    }

    .sidebar {
      width: 100%;
      flex-direction: row;
      flex-wrap: wrap;
    }

    .sidebar-card {
      flex: 1;
      min-width: 250px;
    }
  }

  @media (max-width: 768px) {
    .header {
      flex-direction: column;
      gap: 15px;
    }

    .questions-grid {
      grid-template-columns: 1fr;
    }

    .language-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .sidebar {
      flex-direction: column;
    }

    .sidebar-card {
      min-width: auto;
    }
  }
`;

// Main App Component
function App() {
  const [selectedLang, setSelectedLang] = useState(null);
  const [staffLang] = useState('nl'); // Staff always sees Dutch
  const [isStaffView, setIsStaffView] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [discussedTopics, setDiscussedTopics] = useState([]);
  const [showPreReport, setShowPreReport] = useState(false);
  const [showPostReport, setShowPostReport] = useState(false);
  
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  const t = selectedLang ? translations[selectedLang] : translations.en;
  const staffT = translations[staffLang];
  const langInfo = selectedLang ? languages[selectedLang] : null;

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => setIsListening(false);
      recognitionRef.current.onend = () => setIsListening(false);
    }
  }, []);

  // Update speech recognition language
  useEffect(() => {
    if (recognitionRef.current && selectedLang) {
      recognitionRef.current.lang = languages[selectedLang]?.code || 'en-US';
    }
  }, [selectedLang]);

  // Handle language selection
  const selectLanguage = (lang) => {
    setSelectedLang(lang);
    // Add welcome message
    setTimeout(() => {
      const welcomeMsg = {
        type: 'ai',
        text: translations[lang].subtitle + '! ' + translations[lang].quickQuestions + ':',
        textStaff: translations.nl.subtitle + '! ' + translations.nl.quickQuestions + ':'
      };
      setMessages([welcomeMsg]);
    }, 300);
  };

  // Handle quick question click
  const handleQuickQuestion = (question) => {
    const category = question.category;
    
    // Add user message
    const userMsg = {
      type: 'user',
      text: question.text,
      textStaff: translations.nl.questions.find(q => q.category === category)?.text || question.text
    };
    setMessages(prev => [...prev, userMsg]);
    
    // Track topic
    if (!discussedTopics.includes(category)) {
      setDiscussedTopics(prev => [...prev, category]);
    }
    
    // Simulate AI response
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const response = t.responses[category];
      const responseStaff = translations.nl.responses[category];
      
      setMessages(prev => [...prev, {
        type: 'ai',
        text: response,
        textStaff: responseStaff
      }]);
    }, 1200 + Math.random() * 800);
  };

  // Send custom message
  const sendMessage = () => {
    if (!inputValue.trim()) return;
    
    const userMsg = {
      type: 'user',
      text: inputValue,
      textStaff: `[${languages[selectedLang].name}] ${inputValue}`
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    
    // Simulate response
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const aiMsg = {
        type: 'ai',
        text: t.responses.procedure, // Default response
        textStaff: translations.nl.responses.procedure
      };
      setMessages(prev => [...prev, aiMsg]);
    }, 1500);
  };

  // Voice input
  const toggleVoice = () => {
    if (!recognitionRef.current) return;
    
    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Calculate urgency
  const getUrgency = () => {
    if (discussedTopics.includes('medical')) return 'high';
    if (discussedTopics.length >= 3) return 'medium';
    return 'low';
  };

  // Language selection screen
  if (!selectedLang) {
    return (
      <>
        <style>{styles}</style>
        <div className="app-container">
          <header className="header">
            <div className="logo-section">
              <div className="logo-icon">🌍</div>
              <div className="logo-text">
                <h1>AI Welcome</h1>
                <p>Nxt Era Solutions</p>
              </div>
            </div>
          </header>
          
          <div className="language-select-screen">
            <div className="welcome-icon">🤝</div>
            <h1 className="welcome-title">AI Welcome</h1>
            <p className="welcome-subtitle">Select your language / Selecteer uw taal</p>
            
            <div className="language-grid">
              {Object.entries(languages).map(([code, lang]) => (
                <div
                  key={code}
                  className="language-card"
                  onClick={() => selectLanguage(code)}
                >
                  <div className="language-flag">{lang.flag}</div>
                  <div className="language-native">{lang.native}</div>
                  <div className="language-name">{lang.name}</div>
                </div>
              ))}
            </div>
          </div>
          
          <footer className="footer">
            AI Welcome Demo | Nxt Era Solutions | Part of HES Consultancy International
          </footer>
        </div>
      </>
    );
  }

  // Main chat interface
  return (
    <>
      <style>{styles}</style>
      <div className="app-container">
        <header className="header">
          <div className="logo-section">
            <div className="logo-icon">🌍</div>
            <div className="logo-text">
              <h1>{t.title}</h1>
              <p>{t.subtitle}</p>
            </div>
          </div>
          <div className="header-controls">
            <div className="view-toggle">
              <button
                className={`view-btn ${!isStaffView ? 'active' : ''}`}
                onClick={() => setIsStaffView(false)}
              >
                {t.clientView}
              </button>
              <button
                className={`view-btn ${isStaffView ? 'active' : ''}`}
                onClick={() => setIsStaffView(true)}
              >
                {staffT.staffView}
              </button>
            </div>
          </div>
        </header>

        <main className="main-content">
          <div className="chat-section">
            {/* Quick Questions */}
            <div className="quick-questions">
              <h3>{t.quickQuestions}</h3>
              <div className="questions-grid">
                {t.questions.slice(0, 4).map((q, idx) => (
                  <button
                    key={idx}
                    className="question-btn"
                    onClick={() => handleQuickQuestion(q)}
                  >
                    <span className="question-icon">{q.icon}</span>
                    <span>{q.text}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Messages */}
            <div className="chat-messages">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`message ${msg.type} ${langInfo?.dir === 'rtl' && !isStaffView ? 'rtl' : ''}`}
                >
                  <div className="message-avatar">
                    {msg.type === 'ai' ? '🌍' : '👤'}
                  </div>
                  <div className="message-bubble">
                    <div className="message-text">
                      {isStaffView ? msg.textStaff : msg.text}
                    </div>
                    {isStaffView && msg.text !== msg.textStaff && (
                      <div className="message-translation">
                        <div className="translation-label">
                          {languages[selectedLang].name} ({staffT.original})
                        </div>
                        {msg.text}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="message ai">
                  <div className="message-avatar">🌍</div>
                  <div className="message-bubble">
                    <div className="typing-indicator">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="chat-input-area">
              <div className="input-wrapper">
                <input
                  type="text"
                  className="chat-input"
                  placeholder={t.askQuestion}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  dir={langInfo?.dir || 'ltr'}
                />
                <button
                  className={`input-btn voice-btn ${isListening ? 'listening' : ''}`}
                  onClick={toggleVoice}
                  title={t.speak}
                >
                  {isListening ? '⏹️' : '🎤'}
                </button>
                <button
                  className="input-btn send-btn"
                  onClick={sendMessage}
                  disabled={!inputValue.trim()}
                >
                  ➤
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="sidebar">
            {/* Current Language */}
            <div className="sidebar-card">
              <h4>{isStaffView ? staffT.reportLabels.language : t.selectLanguage}</h4>
              <div className="current-language">
                <span className="lang-flag-large">{langInfo?.flag}</span>
                <div className="lang-details">
                  <h5>{langInfo?.native}</h5>
                  <p>{langInfo?.name}</p>
                </div>
              </div>
              <button className="change-lang-btn" onClick={() => setSelectedLang(null)}>
                🔄 {t.selectLanguage}
              </button>
            </div>

            {/* Topics Discussed */}
            {discussedTopics.length > 0 && (
              <div className="sidebar-card">
                <h4>{isStaffView ? staffT.reportLabels.topics : t.quickQuestions}</h4>
                <div className="topics-list">
                  {discussedTopics.map((topic, idx) => {
                    const q = t.questions.find(q => q.category === topic);
                    return (
                      <span key={idx} className="topic-tag">
                        {q?.icon} {isStaffView ? translations.nl.questions.find(q => q.category === topic)?.text?.split(' ').slice(0,2).join(' ') : q?.text?.split(' ').slice(0,2).join(' ')}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Reports */}
            <div className="sidebar-card">
              <h4>{isStaffView ? 'Rapportage' : 'Reports'}</h4>
              <div className="report-buttons">
                <button className="report-btn pre" onClick={() => setShowPreReport(true)}>
                  📋 {t.preRegistration}
                </button>
                <button
                  className="report-btn post"
                  onClick={() => setShowPostReport(true)}
                  disabled={messages.length < 2}
                >
                  📄 {t.postIntake}
                </button>
              </div>
            </div>
          </aside>
        </main>

        <footer className="footer">
          AI Welcome Demo | Nxt Era Solutions | Part of HES Consultancy International
          <br />
          <small style={{opacity: 0.6}}>
            Demo only - In production connects to real translation APIs
          </small>
        </footer>

        {/* Pre-Registration Modal */}
        {showPreReport && (
          <div className="modal-overlay" onClick={() => setShowPreReport(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>📋 {t.preRegistration}</h3>
                <button className="modal-close" onClick={() => setShowPreReport(false)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="report-section">
                  <h4>{staffT.reportLabels.date}</h4>
                  <p>{new Date().toLocaleDateString('nl-NL')} - {new Date().toLocaleTimeString('nl-NL', {hour: '2-digit', minute: '2-digit'})}</p>
                </div>
                <div className="report-section">
                  <h4>{staffT.reportLabels.language}</h4>
                  <p>{langInfo?.flag} {langInfo?.name} ({langInfo?.native})</p>
                </div>
                <div className="report-section">
                  <h4>Intake Status</h4>
                  <p>Nieuw gesprek gestart via AI Welcome</p>
                </div>
                <div className="report-section">
                  <h4>Voorbereiding</h4>
                  <ul>
                    <li>✅ Tolk beschikbaar: {langInfo?.name}</li>
                    <li>✅ Informatiefolders klaargezet</li>
                    <li>⏳ Wachtend op intake gesprek</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button className="modal-btn secondary" onClick={() => setShowPreReport(false)}>
                  {t.close}
                </button>
                <button className="modal-btn primary">
                  🖨️ Print
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Post-Intake Report Modal */}
        {showPostReport && (
          <div className="modal-overlay" onClick={() => setShowPostReport(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>📄 {staffT.reportLabels.title}</h3>
                <button className="modal-close" onClick={() => setShowPostReport(false)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="report-section">
                  <h4>{staffT.reportLabels.date}</h4>
                  <p>{new Date().toLocaleDateString('nl-NL')} - {new Date().toLocaleTimeString('nl-NL', {hour: '2-digit', minute: '2-digit'})}</p>
                </div>
                
                <div className="report-section">
                  <h4>{staffT.reportLabels.language}</h4>
                  <p>{langInfo?.flag} {langInfo?.name} ({langInfo?.native})</p>
                </div>

                <div className="report-section">
                  <h4>{staffT.reportLabels.urgency}</h4>
                  <span className={`urgency-badge ${getUrgency()}`}>
                    {getUrgency() === 'high' && '🔴 Hoog - Medische aandacht nodig'}
                    {getUrgency() === 'medium' && '🟡 Gemiddeld - Meerdere vragen'}
                    {getUrgency() === 'low' && '🟢 Normaal'}
                  </span>
                </div>

                <div className="report-section">
                  <h4>{staffT.reportLabels.topics}</h4>
                  <ul>
                    {discussedTopics.map((topic, idx) => {
                      const q = translations.nl.questions.find(q => q.category === topic);
                      return <li key={idx}>{q?.icon} {q?.text}</li>;
                    })}
                    {discussedTopics.length === 0 && <li>Geen specifieke onderwerpen besproken</li>}
                  </ul>
                </div>

                <div className="report-section">
                  <h4>{staffT.reportLabels.nextSteps}</h4>
                  <ul>
                    <li>1️⃣ Identificatie en registratie voltooien</li>
                    <li>2️⃣ Vingerafdrukken afnemen</li>
                    {discussedTopics.includes('medical') && <li>🏥 Medische screening prioriteit</li>}
                    {discussedTopics.includes('family') && <li>👨‍👩‍👧 Gezinsregistratie controleren</li>}
                    <li>3️⃣ Toewijzing opvanglocatie</li>
                  </ul>
                </div>

                <div className="report-section">
                  <h4>{staffT.reportLabels.conversationLog}</h4>
                  <div className="conversation-log">
                    {messages.map((msg, idx) => (
                      <div key={idx} className="log-entry">
                        <span className={`log-speaker ${msg.type}`}>
                          {msg.type === 'user' ? 'Cliënt:' : 'AI Welcome:'}
                        </span>
                        {msg.textStaff || msg.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="modal-btn secondary" onClick={() => setShowPostReport(false)}>
                  {t.close}
                </button>
                <button className="modal-btn primary">
                  📥 {t.downloadReport}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default App;
